import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { api, API_BASE, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { ClipboardCheck, Plus, Trash2, Upload, ChevronDown, X, CalendarDays, Archive, FileSpreadsheet, FileText, Search, ClipboardList, Star } from "lucide-react";
import { toast } from "sonner";

const HC_META = {
  open: { label: "Open", cls: "bg-gray-100 text-gray-600 border-gray-300" },
  in_progress: { label: "In-progress", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  postponed: { label: "Postponed", cls: "bg-rose-100 text-rose-800 border-rose-300" },
};

const JC_PATTERN = /^\d+\s*[A-Z]{0,2}$/;

function parseJobcardFile(rows) {
  const cards = [];
  for (let i = 0; i < rows.length; i++) {
    const c0 = String((rows[i] || [])[0] ?? "").trim();
    if (!JC_PATTERN.test(c0)) continue;
    const rowB = rows[i + 1] || [];
    const rowC = rows[i + 2] || [];
    cards.push({
      jc_number: c0,
      status_text: String((rows[i] || [])[2] ?? "").trim(),
      subject: String(rowB[4] ?? "").trim(),
      work_type: String(rowB[7] ?? "").trim(),
      details: String(rowC[0] ?? "").trim(),
    });
  }
  return cards;
}

function StatusButton({ hc, jc, onChanged }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [askReason, setAskReason] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = async (status, why = "") => {
    setBusy(true);
    try {
      const r = await api.patch(`/heavy-checks/${hc.id}/jobcards/${jc.id}`, { progress_status: status, postponed_reason: why });
      onChanged(r.data);
      toast.success(`JC ${jc.jc_number} — ${HC_META[status].label}`);
      setOpen(false);
      setAskReason(false);
      setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally {
      setBusy(false);
    }
  };

  const meta = HC_META[jc.progress_status || "open"];
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setAskReason(false); setReason(""); } }}>
      <PopoverTrigger asChild>
        <button
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-widest ${meta.cls} hover:opacity-80 transition-opacity`}
          data-testid={`hc-jc-status-btn-${jc.id}`}
        >
          {meta.label} <ChevronDown className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="end" data-testid={`hc-jc-status-pop-${jc.id}`}>
        {!askReason ? (
          <div className="space-y-1">
            {["in_progress", "completed", "postponed"].map((s) => (
              <button
                key={s}
                className={`w-full text-left px-2 py-1.5 rounded text-xs font-medium hover:bg-summit-glacier/50 ${jc.progress_status === s ? "bg-summit-glacier/40" : ""}`}
                disabled={busy}
                onClick={() => (s === "postponed" ? setAskReason(true) : set(s))}
                data-testid={`hc-jc-set-${s}-${jc.id}`}
              >
                <span className={`inline-block w-2 h-2 rounded-full mr-2 ${s === "in_progress" ? "bg-sky-500" : s === "completed" ? "bg-emerald-500" : "bg-rose-500"}`} />
                {HC_META[s].label}
              </button>
            ))}
            {jc.progress_status && jc.progress_status !== "open" && (
              <button className="w-full text-left px-2 py-1.5 rounded text-xs text-gray-500 hover:bg-summit-glacier/50" disabled={busy} onClick={() => set("open")} data-testid={`hc-jc-set-open-${jc.id}`}>
                <span className="inline-block w-2 h-2 rounded-full mr-2 bg-gray-400" /> Reset to Open
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="font-mono text-[9px] uppercase tracking-widest text-rose-700">Postpone reason *</div>
            <Textarea rows={2} className="text-xs" placeholder="Why is this jobcard postponed?" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`hc-jc-postpone-reason-${jc.id}`} />
            <Button
              size="sm"
              className="w-full h-8 bg-rose-600 hover:bg-rose-700 text-white"
              disabled={busy || !reason.trim()}
              onClick={() => set("postponed", reason.trim())}
              data-testid={`hc-jc-postpone-confirm-${jc.id}`}
            >
              Postpone jobcard
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function JobcardRow({ hc, jc, onChanged }) {
  const removeJc = async () => {
    if (!window.confirm(`Remove jobcard ${jc.jc_number} from this report?`)) return;
    try {
      const r = await api.delete(`/heavy-checks/${hc.id}/jobcards/${jc.id}`);
      onChanged(r.data);
      toast.success(`JC ${jc.jc_number} removed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove jobcard");
    }
  };
  return (
    <tr className={`border-b border-summit-hairline align-top ${jc.removed_from_workscope ? "bg-gray-50/80" : ""}`} data-testid={`hc-jc-row-${jc.id}`}>
      <td className={`py-2 px-3 font-mono text-xs font-bold whitespace-nowrap ${jc.removed_from_workscope ? "text-gray-400 line-through" : ""}`}>{jc.jc_number}</td>
      <td className="py-2 px-3">
        <div className={`text-sm leading-snug ${jc.removed_from_workscope ? "text-gray-400" : ""}`}>{jc.details || "—"}</div>
        {jc.removed_from_workscope && (
          <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 font-mono text-[9px] uppercase tracking-widest" data-testid={`hc-jc-removed-${jc.id}`}>
            Removed from workscope{jc.removed_at ? ` · ${(jc.removed_at || "").slice(0, 10)}` : ""}
          </span>
        )}
        {jc.subject && <div className="font-mono text-[10px] text-gray-500 mt-0.5">{jc.subject}</div>}
        {jc.progress_status === "postponed" && jc.postponed_reason && (
          <div className="text-[11px] italic text-rose-700 mt-0.5" data-testid={`hc-jc-reason-${jc.id}`}>Postponed — “{jc.postponed_reason}”</div>
        )}
      </td>
      <td className="py-2 px-3 whitespace-nowrap">
        {jc.status_text ? (
          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 font-mono text-[10px] uppercase tracking-widest">{jc.status_text}</span>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
      <td className="py-2 px-3 whitespace-nowrap font-mono text-[11px] text-gray-600">{jc.work_type || "—"}</td>
      <td className="py-2 px-3 text-right whitespace-nowrap">
        <span className="inline-flex items-center gap-1">
          <StatusButton hc={hc} jc={jc} onChanged={onChanged} />
          <button className="p-1 rounded text-gray-300 hover:text-rose-600 hover:bg-rose-50" onClick={removeJc} title="Remove jobcard" data-testid={`hc-jc-delete-${jc.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </span>
        {jc.status_updated_by_name && (
          <div className="font-mono text-[9px] text-gray-400 mt-1">{jc.status_updated_by_name}</div>
        )}
      </td>
    </tr>
  );
}

const PART_COLS_LEFT = [
  { key: "description", label: "Description", type: "text", w: "min-w-[130px]" },
  { key: "part_number", label: "Part Number", type: "text", w: "min-w-[110px]" },
  { key: "serial_number", label: "Serial Number", type: "text", w: "min-w-[90px]" },
  { key: "qty_req", label: "Qty Req", type: "number", w: "w-[56px]" },
  { key: "qty_rec", label: "Qty Rec", type: "number", w: "w-[56px]" },
  { key: "condition", label: "Cond.", type: "text", w: "w-[70px]" },
  { key: "trace", label: "Trace", type: "text", w: "w-[70px]" },
  { key: "price_usd", label: "Price", type: "number", w: "w-[84px]" },
  { key: "currency", label: "Curr", type: "select", options: ["USD", "CAD", "GBP"], w: "w-[58px]" },
];
const PART_COLS_RIGHT = [
  { key: "tracking", label: "Tracking", type: "text", w: "min-w-[90px]" },
  { key: "eta", label: "ETA", type: "date", w: "w-[112px]" },
  { key: "comments", label: "Comments", type: "text", w: "min-w-[110px]" },
  { key: "jc", label: "JC", type: "text", w: "w-[64px]", eng: true },
  { key: "date_received", label: "Date Rec'd", type: "date", w: "w-[112px]", eng: true },
  { key: "date_issued", label: "Date Issued", type: "date", w: "w-[112px]", eng: true },
];

const CUR_SYM = { USD: "US$", CAD: "CA$", GBP: "£" };
const fmtMoney = (n, cur = "CAD") => `${CUR_SYM[cur] || "$"}${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hcFx = (hc) => ({ USD: Number(hc?.fx_rates?.USD) || 1.37, GBP: Number(hc?.fx_rates?.GBP) || 1.72 });
const partCad = (p, fx) => {
  const t = (Number(p.qty_req) || 0) * (Number(p.price_usd) || 0);
  const c = p.currency || "USD";
  return c === "CAD" ? t : t * (c === "GBP" ? fx.GBP : fx.USD);
};

function PartCell({ hc, part, col, onChanged }) {
  const [v, setV] = useState(part[col.key] ?? "");
  useEffect(() => { setV(part[col.key] ?? ""); }, [part, col.key]);
  const commit = async (val = v) => {
    const orig = part[col.key] ?? "";
    if (String(val) === String(orig)) return;
    try {
      const r = await api.patch(`/heavy-checks/${hc.id}/parts/${part.id}`, { [col.key]: val === "" ? null : (col.type === "number" ? Number(val) : val) });
      onChanged(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
      setV(orig);
    }
  };
  if (col.type === "select") {
    return (
      <select
        className="w-full h-7 px-0.5 text-xs bg-transparent border border-transparent hover:border-summit-hairline focus:border-summit-primary focus:bg-white rounded outline-none cursor-pointer"
        value={v || "USD"}
        onChange={(e) => { setV(e.target.value); commit(e.target.value); }}
        data-testid={`hc-part-${col.key}-${part.id}`}
      >
        {(col.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return (
    <input
      type={col.type}
      min={col.type === "number" ? "0" : undefined}
      step={col.type === "number" ? "any" : undefined}
      className={`w-full h-7 px-1 text-xs bg-transparent border border-transparent hover:border-summit-hairline focus:border-summit-primary focus:bg-white rounded outline-none ${col.type === "number" ? "text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" : ""}`}
      value={v ?? ""}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => commit()}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      data-testid={`hc-part-${col.key}-${part.id}`}
    />
  );
}

function FxEditor({ hc, onChanged }) {
  const [usd, setUsd] = useState(hcFx(hc).USD);
  const [gbp, setGbp] = useState(hcFx(hc).GBP);
  useEffect(() => { setUsd(hcFx(hc).USD); setGbp(hcFx(hc).GBP); }, [hc.id, hc.fx_rates]);
  const commit = async () => {
    const u = Number(usd), g = Number(gbp);
    if (!u || !g || u <= 0 || g <= 0) { toast.error("Rates must be positive numbers"); return; }
    const cur = hcFx(hc);
    if (cur.USD === u && cur.GBP === g) return;
    try {
      const r = await api.patch(`/heavy-checks/${hc.id}`, { fx_rates: { USD: u, GBP: g } });
      onChanged(r.data);
      toast.success("Exchange rates saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save rates");
      setUsd(hcFx(hc).USD);
      setGbp(hcFx(hc).GBP);
    }
  };
  const cls = "h-7 w-[70px] px-1.5 text-xs text-center bg-white border border-summit-hairline focus:border-summit-primary rounded outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none";
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="hc-fx-editor">
      <span className="font-mono text-[9px] uppercase tracking-widest text-gray-500">FX → CAD</span>
      <label className="flex items-center gap-1 font-mono text-[10px] text-gray-600">
        US$
        <input type="number" min="0" step="any" className={cls} value={usd} onChange={(e) => setUsd(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }} data-testid="hc-fx-usd" />
      </label>
      <label className="flex items-center gap-1 font-mono text-[10px] text-gray-600">
        £
        <input type="number" min="0" step="any" className={cls} value={gbp} onChange={(e) => setGbp(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }} data-testid="hc-fx-gbp" />
      </label>
    </div>
  );
}

function PartsSection({ hc, onChanged }) {
  const [busy, setBusy] = useState(false);
  const parts = hc.parts || [];
  const fx = hcFx(hc);
  const grandCad = parts.reduce((s, p) => s + partCad(p, fx), 0);
  const usedCad = parts.filter((p) => (p.jc || "").trim()).reduce((s, p) => s + partCad(p, fx), 0);

  const addRow = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/heavy-checks/${hc.id}/parts`, {});
      onChanged(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add line");
    } finally {
      setBusy(false);
    }
  };

  const removeRow = async (p) => {
    if (!window.confirm(`Remove part line "${p.description || p.part_number || "blank"}"?`)) return;
    try {
      const r = await api.delete(`/heavy-checks/${hc.id}/parts/${p.id}`);
      onChanged(r.data);
      toast.success("Part line removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  return (
    <div data-testid="hc-parts-section">
      <div className="bg-[#1f3864] text-white text-center py-1.5 font-display text-sm font-bold tracking-wide" data-testid="hc-parts-title">
        {hc.title} — Parts
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="font-display text-[10px] font-bold">
              {PART_COLS_LEFT.map((c) => (
                <th key={c.key} className={`py-1.5 px-1 text-center bg-[#2f5597] text-white border border-[#24477e] ${c.w}`}>{c.label}</th>
              ))}
              <th className="py-1.5 px-1 text-center bg-[#2f5597] text-white border border-[#24477e] w-[90px]">Total</th>
              {PART_COLS_RIGHT.map((c) => (
                <th key={c.key} className={`py-1.5 px-1 text-center border ${c.eng ? "bg-amber-300 text-amber-950 border-amber-400" : "bg-[#2f5597] text-white border-[#24477e]"} ${c.w}`}>{c.label}</th>
              ))}
              <th className="bg-[#2f5597] border border-[#24477e] w-7" />
            </tr>
          </thead>
          <tbody>
            {parts.length === 0 && (
              <tr><td colSpan={PART_COLS_LEFT.length + PART_COLS_RIGHT.length + 2} className="text-center py-4 text-sm text-gray-400" data-testid="hc-parts-empty">No parts yet — add a line below.</td></tr>
            )}
            {parts.map((p, i) => {
              const total = (Number(p.qty_req) || 0) * (Number(p.price_usd) || 0);
              const rowBg = i % 2 ? "bg-sky-100/60" : "bg-sky-50/60";
              return (
                <tr key={p.id} data-testid={`hc-part-row-${p.id}`}>
                  {PART_COLS_LEFT.map((c) => (
                    <td key={c.key} className={`border border-sky-200 ${rowBg} px-0.5`}><PartCell hc={hc} part={p} col={c} onChanged={onChanged} /></td>
                  ))}
                  <td className={`border border-sky-200 ${rowBg} px-1.5 text-right font-mono text-xs font-semibold whitespace-nowrap`} data-testid={`hc-part-total-${p.id}`}>
                    {total > 0 ? fmtMoney(total, p.currency || "USD") : <span className="text-gray-400">$ –</span>}
                  </td>
                  {PART_COLS_RIGHT.map((c) => (
                    <td key={c.key} className={`border px-0.5 ${c.eng ? "bg-amber-50 border-amber-200" : `${rowBg} border-sky-200`}`}><PartCell hc={hc} part={p} col={c} onChanged={onChanged} /></td>
                  ))}
                  <td className={`border border-sky-200 ${rowBg} text-center`}>
                    <button className="p-0.5 rounded text-gray-300 hover:text-rose-600" onClick={() => removeRow(p)} title="Remove line" data-testid={`hc-part-remove-${p.id}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {parts.length > 0 && (
              <>
                <tr className="bg-white">
                  <td colSpan={PART_COLS_LEFT.length} className="py-1.5 px-2 text-right font-mono text-[10px] uppercase tracking-widest text-gray-500 border border-sky-200">Grand total (CAD)</td>
                  <td className="py-1.5 px-1.5 text-right font-mono text-xs font-bold text-[#1f3864] border border-sky-200 whitespace-nowrap" data-testid="hc-parts-grand-total">{fmtMoney(grandCad)}</td>
                  <td colSpan={PART_COLS_RIGHT.length + 1} className="border border-sky-200" />
                </tr>
                <tr className="bg-white">
                  <td colSpan={PART_COLS_LEFT.length} className="py-1 px-2 text-right font-mono text-[10px] uppercase tracking-widest text-emerald-700 border border-sky-200">Used on JC (CAD)</td>
                  <td className="py-1 px-1.5 text-right font-mono text-xs font-bold text-emerald-700 border border-sky-200 whitespace-nowrap" data-testid="hc-parts-used-total">{fmtMoney(usedCad)}</td>
                  <td colSpan={PART_COLS_RIGHT.length + 1} className="border border-sky-200" />
                </tr>
                <tr className="bg-white">
                  <td colSpan={PART_COLS_LEFT.length} className="py-1 px-2 text-right font-mono text-[10px] uppercase tracking-widest text-amber-700 border border-sky-200">Unused (CAD)</td>
                  <td className="py-1 px-1.5 text-right font-mono text-xs font-bold text-amber-700 border border-sky-200 whitespace-nowrap" data-testid="hc-parts-notused-total">{fmtMoney(grandCad - usedCad)}</td>
                  <td colSpan={PART_COLS_RIGHT.length + 1} className="border border-sky-200" />
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-2 flex items-center gap-3 flex-wrap">
        <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={addRow} data-testid="hc-parts-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add part line
        </Button>
        <FxEditor hc={hc} onChanged={onChanged} />
        <span className="font-mono text-[9px] uppercase tracking-widest text-gray-400">
          Total = Qty Req × Price → CAD · <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">amber columns</span> = engineer onsite
        </span>
      </div>
    </div>
  );
}

function EstCompletion({ hc, onChanged }) {
  const [v, setV] = useState(hc.est_completion_date || "");
  useEffect(() => { setV(hc.est_completion_date || ""); }, [hc.id, hc.est_completion_date]);
  const commit = async () => {
    if ((v || "") === (hc.est_completion_date || "")) return;
    try {
      const r = await api.patch(`/heavy-checks/${hc.id}`, { est_completion_date: v || null });
      onChanged(r.data);
      toast.success(v ? `Est. completion set — ${v}` : "Est. completion cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
      setV(hc.est_completion_date || "");
    }
  };
  return (
    <div className="flex items-center gap-2.5 border border-amber-300 bg-amber-50 rounded-md px-3 py-2" data-testid="hc-est-completion">
      <CalendarDays className="w-4 h-4 text-amber-600 shrink-0" />
      <div>
        <div className="font-mono text-[9px] uppercase tracking-widest text-amber-800">Engineer onsite — Est. completion</div>
        <input
          type="date"
          className="mt-0.5 h-7 px-1.5 text-sm font-semibold bg-white border border-amber-300 rounded outline-none focus:border-amber-500"
          value={v}
          onChange={(e) => setV(e.target.value)}
          onBlur={commit}
          data-testid="hc-est-completion-input"
        />
        {hc.est_set_by_name && hc.est_completion_date && (
          <div className="font-mono text-[9px] text-amber-700/70 mt-0.5" data-testid="hc-est-completion-by">set by {hc.est_set_by_name} · {(hc.est_set_at || "").slice(0, 10)}</div>
        )}
      </div>
      {(() => {
        if (!v) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diff = Math.round((new Date(`${v}T00:00:00`) - today) / 86400000);
        const meta = diff < 0
          ? { txt: `${-diff} day${-diff === 1 ? "" : "s"} overdue`, cls: "bg-red-600 text-white border-red-700" }
          : diff === 0
            ? { txt: "Due today", cls: "bg-amber-400 text-amber-950 border-amber-500" }
            : diff <= 7
              ? { txt: `${diff} day${diff === 1 ? "" : "s"} left`, cls: "bg-amber-100 text-amber-800 border-amber-300" }
              : { txt: `${diff} days left`, cls: "bg-emerald-100 text-emerald-800 border-emerald-300" };
        return (
          <span className={`shrink-0 px-2 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-widest font-bold ${meta.cls}`} data-testid="hc-days-badge">
            {meta.txt}
          </span>
        );
      })()}
    </div>
  );
}

const FIN_FIELDS = [
  { key: "deposit_requirement", label: "Deposit Requirement" },
  { key: "nrc_parts_deposit", label: "NRC & Parts Deposit" },
  { key: "redelivery_invoice", label: "Redelivery Invoice" },
  { key: "final_invoice", label: "Final Invoice" },
];

function HcFinance({ hc, onChanged }) {
  const [po, setPo] = useState(hc.po_number || "");
  useEffect(() => { setPo(hc.po_number || ""); }, [hc.id, hc.po_number]);
  const savePo = async () => {
    if ((po || "").trim() === (hc.po_number || "")) return;
    try {
      const r = await api.patch(`/heavy-checks/${hc.id}`, { po_number: po.trim() });
      onChanged(r.data);
      toast.success(po.trim() ? "PO saved" : "PO cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save PO");
      setPo(hc.po_number || "");
    }
  };
  const setStatus = async (key, val) => {
    try {
      const r = await api.patch(`/heavy-checks/${hc.id}`, { finance_status: { [key]: val } });
      onChanged(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save status");
    }
  };
  const fs = hc.finance_status || {};
  return (
    <div className="mt-3 pt-3 border-t border-summit-hairline space-y-1.5" data-testid="hc-summary-finance">
      <div className="flex justify-between items-center gap-2 font-mono text-[10px]">
        <span className="uppercase tracking-widest text-gray-500 leading-tight">PO for this heavy check</span>
        <input
          className="w-20 h-6 px-1 text-[11px] font-bold text-right border border-transparent hover:border-summit-hairline focus:border-summit-primary rounded outline-none bg-transparent"
          value={po}
          placeholder="—"
          onChange={(e) => setPo(e.target.value)}
          onBlur={savePo}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          title={hc.po_set_by_name && hc.po_number ? `PO set by ${hc.po_set_by_name} · ${(hc.po_set_at || "").slice(0, 10)}` : "Click to edit — Enter or click away to save"}
          data-testid="hc-fin-po-input"
        />
      </div>
      {FIN_FIELDS.map(({ key, label }) => {
        const val = fs[key] || "Pending";
        const meta = (hc.finance_status_meta || {})[key];
        const cls = val === "Paid"
          ? "bg-emerald-100 text-emerald-800 border-emerald-300"
          : "bg-red-100 text-red-700 border-red-300";
        return (
          <div key={key} className="flex justify-between items-center gap-2 font-mono text-[10px]">
            <span className="uppercase tracking-widest text-gray-500 leading-tight">{label}</span>
            <select
              className={`h-6 px-1 rounded-full border font-mono text-[9px] uppercase tracking-widest font-bold cursor-pointer outline-none ${cls}`}
              value={val}
              onChange={(e) => setStatus(key, e.target.value)}
              title={meta ? `${val} — set by ${meta.by_name} · ${(meta.at || "").slice(0, 10)}` : `${label}: ${val}`}
              data-testid={`hc-fin-${key}`}
            >
              <option value="Pending">Pending</option>
              <option value="Paid">Paid</option>
            </select>
          </div>
        );
      })}
    </div>
  );
}

export default function HeavyCheckStatus() {
  const { user } = useAuth();
  const [checks, setChecks] = useState(null);
  const [aircraft, setAircraft] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newTail, setNewTail] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [section, setSection] = useState("parts");
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [scanPrompt, setScanPrompt] = useState(null);
  const [applying, setApplying] = useState(false);
  const [favId, setFavId] = useState(user?.fav_heavy_check_id || null);
  const fileRef = useRef(null);

  const hasAccess = user && (user.planning_access || user.role === "admin");

  useEffect(() => {
    if (!hasAccess) return;
    api.get("/heavy-checks").then((r) => {
      setChecks(r.data || []);
      const active = (r.data || []).filter((c) => !c.archived);
      const fav = active.find((c) => c.id === user?.fav_heavy_check_id);
      setSelectedId(fav?.id || active[0]?.id || (r.data || [])[0]?.id || null);
    }).catch(() => setChecks([]));
    api.get("/maintenance/aircraft").then((r) => setAircraft(r.data || [])).catch(() => {});
  }, [hasAccess]);

  const visible = useMemo(() => (checks || []).filter((c) => showArchived || !c.archived), [checks, showArchived]);
  const archivedCount = useMemo(() => (checks || []).filter((c) => c.archived).length, [checks]);
  const selected = useMemo(() => visible.find((c) => c.id === selectedId) || null, [visible, selectedId]);
  const replaceCheck = (nd) => setChecks((cs) => (cs || []).map((c) => (c.id === nd.id ? nd : c)));
  useEffect(() => { setSection("parts"); setSearch(""); }, [selectedId]);
  useEffect(() => {
    if (checks !== null && !visible.some((c) => c.id === selectedId)) setSelectedId(visible[0]?.id || null);
  }, [visible, selectedId, checks]);

  const toggleFavourite = async () => {
    if (!selected) return;
    try {
      if (favId === selected.id) {
        await api.delete(`/heavy-checks/${selected.id}/favourite`);
        setFavId(null);
        toast.success(`${selected.title} removed as your landing report`);
      } else {
        await api.put(`/heavy-checks/${selected.id}/favourite`);
        setFavId(selected.id);
        toast.success(`${selected.title} is now your landing report`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update favourite");
    }
  };

  const toggleArchive = async () => {
    if (!selected) return;
    try {
      const r = await api.patch(`/heavy-checks/${selected.id}`, { archived: !selected.archived });
      replaceCheck(r.data);
      toast.success(r.data.archived ? `${r.data.title} archived` : `${r.data.title} restored`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const groups = useMemo(() => {
    const all = selected?.jobcards || [];
    return {
      planned: all.filter((j) => (j.group || "planned") !== "nrc"),
      nrc: all.filter((j) => (j.group || "planned") === "nrc"),
    };
  }, [selected]);

  const counts = useMemo(() => {
    const all = selected?.jobcards || [];
    const active = all.filter((j) => !j.removed_from_workscope);
    const c = { total: active.length, removed: all.length - active.length, open: 0, in_progress: 0, completed: 0, postponed: 0 };
    active.forEach((j) => { c[j.progress_status || "open"] += 1; });
    return c;
  }, [selected]);

  const groupCounts = useMemo(() => {
    const calc = (list) => {
      const active = list.filter((j) => !j.removed_from_workscope);
      const done = active.filter((j) => j.progress_status === "completed").length;
      return { total: active.length, done, pct: active.length ? Math.round((done / active.length) * 100) : 0 };
    };
    return { planned: calc(groups.planned), nrc: calc(groups.nrc) };
  }, [groups]);

  const sectionCards = useMemo(() => {
    const list = section === "nrc" ? groups.nrc : groups.planned;
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((j) => [j.jc_number, j.details, j.subject, j.work_type, j.status_text].some((v) => (v || "").toLowerCase().includes(q)));
  }, [groups, section, search]);

  const create = async () => {
    if (!newTail) { toast.error("Pick an aircraft"); return; }
    setBusy(true);
    try {
      const r = await api.post("/heavy-checks", { tail: newTail, title: newTitle });
      setChecks((cs) => [...(cs || []), r.data]);
      setSelectedId(r.data.id);
      setShowNew(false);
      setNewTail("");
      setNewTitle("");
      toast.success(`${r.data.title} created — scan a JobCardStatus file to import jobcards`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create report");
    } finally {
      setBusy(false);
    }
  };

  const removeReport = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete "${selected.title}" and all its jobcards?`)) return;
    try {
      await api.delete(`/heavy-checks/${selected.id}`);
      setChecks((cs) => {
        const next = (cs || []).filter((c) => c.id !== selected.id);
        setSelectedId(next[0]?.id || null);
        return next;
      });
      toast.success("Report deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const deleteAllJobcards = async () => {
    if (!selected) return;
    const grp = section === "nrc" ? "nrc" : "planned";
    const n = (grp === "nrc" ? groups.nrc : groups.planned).length;
    if (!window.confirm(`Delete all ${n} ${grp === "nrc" ? "NRC " : ""}jobcards from "${selected.title}"? This can't be undone.`)) return;
    try {
      const r = await api.delete(`/heavy-checks/${selected.id}/jobcards?group=${grp}`);
      replaceCheck(r.data);
      toast.success(`All ${grp === "nrc" ? "NRC " : ""}jobcards deleted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete jobcards");
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selected) return;
    setScanning(true);
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
      const cards = parseJobcardFile(rows);
      if (cards.length === 0) { toast.error("No jobcards found in this file — expected a JobCardStatus export"); return; }
      setScanPrompt({ cards, group: null, diff: null });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to read the file");
    } finally {
      setScanning(false);
    }
  };

  const chooseGroup = (group) => {
    const existing = (selected?.jobcards || []).filter((j) => (j.group || "planned") === group);
    const existingMap = new Map(existing.map((j) => [j.jc_number, j]));
    const incoming = new Set(scanPrompt.cards.map((c) => c.jc_number.trim()));
    const added = [], removed = [], restored = [];
    let updated = 0;
    const seen = new Set();
    scanPrompt.cards.forEach((c) => {
      const n = c.jc_number.trim();
      if (seen.has(n)) return;
      seen.add(n);
      const ex = existingMap.get(n);
      if (!ex) added.push(n);
      else if (ex.removed_from_workscope) restored.push(n);
      else updated += 1;
    });
    existing.forEach((j) => { if (!incoming.has(j.jc_number) && !j.removed_from_workscope) removed.push(j.jc_number); });
    setScanPrompt((s) => ({ ...s, group, diff: { added, removed, restored, updated } }));
  };

  const applyScan = async () => {
    setApplying(true);
    try {
      const r = await api.post(`/heavy-checks/${selected.id}/scan`, { jobcards: scanPrompt.cards, group: scanPrompt.group });
      const grp = scanPrompt.group;
      replaceCheck(r.data);
      setScanPrompt(null);
      setSection(grp === "nrc" ? "nrc" : "jobcards");
      toast.success(`Scan complete — ${r.data.scan_added} added, ${r.data.scan_removed} flagged removed, ${r.data.scan_updated} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply scan");
    } finally {
      setApplying(false);
    }
  };

  const exportReport = async (fmt) => {
    if (!selected) return;
    try {
      const resp = await fetch(`${API_BASE}/heavy-checks/${selected.id}/export?fmt=${fmt}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!resp.ok) throw new Error();
      const cd = resp.headers.get("Content-Disposition") || "";
      const filename = (cd.match(/filename="([^"]+)"/) || [])[1] || `heavy-check.${fmt}`;
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded`);
    } catch {
      toast.error("Export failed");
    }
  };

  if (!user) return null;
  if (!hasAccess) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="hc-no-access">
        <ClipboardCheck className="w-8 h-8 mx-auto text-gray-300" />
        <h1 className="font-display text-2xl font-semibold mt-4">Heavy Check Status</h1>
        <p className="text-sm text-gray-500 mt-2">You don&apos;t have access to this page. Ask an admin to grant Heavy Check access.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Fleet · MST</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-1" data-testid="hc-heading">Heavy Check Status</h1>
          <p className="text-sm text-gray-500 mt-1">Import jobcards from a JobCardStatus .xls export and track their progress per aircraft.</p>
        </div>
        <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={() => setShowNew(true)} data-testid="hc-new-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New report
        </Button>
      </div>

      {(visible.length > 1 || archivedCount > 0) && (
        <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="hc-report-tabs">
          {visible.map((c) => (
            <button
              key={c.id}
              className={`px-3 py-1.5 rounded-full border font-mono text-[11px] uppercase tracking-widest transition-colors ${c.id === selectedId ? "bg-summit-primary text-white border-summit-primary" : c.archived ? "bg-gray-50 text-gray-400 border-gray-200 hover:border-gray-400" : "bg-white text-gray-600 border-summit-hairline hover:border-summit-primary"}`}
              onClick={() => setSelectedId(c.id)}
              data-testid={`hc-tab-${c.id}`}
            >
              {c.id === favId && <Star className="inline w-3 h-3 mr-1 -mt-0.5 fill-amber-400 text-amber-400" data-testid={`hc-tab-fav-${c.id}`} />}
              {c.tail} · {c.title}{c.archived ? " · archived" : ""}
            </button>
          ))}
          {archivedCount > 0 && (
            <button
              className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-mono text-[10px] uppercase tracking-widest transition-colors ${showArchived ? "bg-gray-700 text-white border-gray-700" : "bg-white text-gray-500 border-summit-hairline hover:border-gray-400"}`}
              onClick={() => setShowArchived((s) => !s)}
              data-testid="hc-show-archived"
            >
              <Archive className="w-3 h-3" /> Archived ({archivedCount})
            </button>
          )}
        </div>
      )}

      {checks === null ? (
        <p className="text-sm text-gray-400 mt-10 text-center">Loading…</p>
      ) : !selected ? (
        <div className="mt-10 text-center text-gray-400" data-testid="hc-empty">
          <ClipboardCheck className="w-8 h-8 mx-auto text-gray-300" />
          <p className="text-sm mt-3">No heavy check reports yet — create one and scan a JobCardStatus file.</p>
        </div>
      ) : (
        <div className="mt-5 flex gap-5 items-start">
          <aside className="hidden xl:block w-56 shrink-0 sticky top-24" data-testid="hc-summary-panel">
            <div className="border border-summit-hairline rounded-lg bg-white p-4 shadow-sm">
              <div className="font-mono text-[9px] uppercase tracking-widest text-gray-400">Summary</div>
              <div className="font-display text-sm font-semibold mt-1 leading-snug" data-testid="hc-summary-title">{selected.tail} · {selected.title}</div>
              <div className="mt-3">
                <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-widest text-gray-500">
                  <span>Jobcards</span>
                  <span className="text-emerald-700 font-bold" data-testid="hc-summary-pct">{groupCounts.planned.pct}%</span>
                </div>
                <div className="mt-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${groupCounts.planned.pct}%` }} />
                </div>
                {groupCounts.nrc.total > 0 && (
                  <div className="mt-2" data-testid="hc-summary-nrc-progress">
                    <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-widest text-gray-500">
                      <span>NRC</span>
                      <span className="text-amber-700 font-bold" data-testid="hc-summary-nrc-pct">{groupCounts.nrc.pct}%</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${groupCounts.nrc.pct}%` }} />
                    </div>
                  </div>
                )}
              </div>
              <HcFinance hc={selected} onChanged={replaceCheck} />
              <div className="mt-3 space-y-1" data-testid="hc-summary-jobcards">
                <div className="flex justify-between font-mono text-[10px] uppercase tracking-widest font-bold text-gray-900"><span>Jobcards</span><span className="text-xs" data-testid="hc-summary-jobcards-total">{counts.total}</span></div>
                {["completed", "in_progress", "postponed", "open"].map((s) => (
                  <div key={s} className="flex justify-between items-center font-mono text-[10px]">
                    <span className={`px-1.5 py-0.5 rounded-full border uppercase tracking-widest ${HC_META[s].cls}`}>{HC_META[s].label}</span>
                    <span className="font-bold text-gray-600">{counts[s]}</span>
                  </div>
                ))}
              </div>
              {(() => {
                const fx = hcFx(selected);
                const parts = selected.parts || [];
                const grand = parts.reduce((s, p) => s + partCad(p, fx), 0);
                const used = parts.filter((p) => (p.jc || "").trim()).reduce((s, p) => s + partCad(p, fx), 0);
                return (
                  <div className="mt-3 pt-3 border-t border-summit-hairline space-y-1" data-testid="hc-summary-parts">
                    <div className="flex justify-between font-mono text-[10px] uppercase tracking-widest text-gray-500"><span>Part lines</span><span className="font-bold text-gray-700">{parts.length}</span></div>
                    <div className="flex justify-between font-mono text-[10px] uppercase tracking-widest font-bold text-gray-900"><span>Parts total (CAD)</span><span className="text-xs text-[#1f3864]" data-testid="hc-summary-parts-total">{fmtMoney(grand)}</span></div>
                    <div className="flex justify-between font-mono text-[10px] uppercase tracking-widest text-gray-500"><span>Used on JC</span><span className="font-bold text-emerald-700" data-testid="hc-summary-used-jc">{fmtMoney(used)}</span></div>
                    <div className="flex justify-between items-start gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500"><span className="leading-tight">Unused</span><span className="font-bold text-amber-700 shrink-0" data-testid="hc-summary-not-used">{fmtMoney(grand - used)}</span></div>
                  </div>
                );
              })()}
              {selected.est_completion_date && (
                <div className="mt-3 pt-3 border-t border-summit-hairline flex justify-between font-mono text-[10px] uppercase tracking-widest text-gray-500" data-testid="hc-summary-est">
                  <span>Est. completion</span><span className="font-bold text-amber-700">{selected.est_completion_date}</span>
                </div>
              )}
            </div>
          </aside>
          <div className="flex-1 min-w-0 border border-summit-hairline rounded-lg bg-white overflow-hidden" data-testid={`hc-report-${selected.id}`}>
          <div className="px-4 py-3 border-b border-summit-hairline flex items-center gap-3 flex-wrap">
            <div>
              <div className="font-display text-lg font-semibold leading-tight flex items-center gap-2" data-testid="hc-report-title">
                {selected.title}
                <button
                  className="p-0.5"
                  onClick={toggleFavourite}
                  title={favId === selected.id ? "Remove as landing report" : "Set as your landing report"}
                  data-testid="hc-fav-btn"
                >
                  <Star className={`w-4 h-4 transition-colors ${favId === selected.id ? "fill-amber-400 text-amber-400" : "text-gray-300 hover:text-amber-400"}`} />
                </button>
                {selected.archived && (
                  <span className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 font-mono text-[9px] uppercase tracking-widest" data-testid="hc-archived-badge">Archived</span>
                )}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">
                {selected.tail}
                {selected.scanned_at ? ` · last scan ${(selected.scanned_at || "").slice(0, 10)} by ${selected.scanned_by_name}` : " · no scan yet"}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <EstCompletion hc={selected} onChanged={replaceCheck} />
              <input ref={fileRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={onFile} data-testid="hc-scan-input" />
              <Button variant="outline" className="h-9" disabled={scanning} onClick={() => fileRef.current?.click()} data-testid="hc-scan-btn">
                <Upload className="w-4 h-4 mr-1.5" /> {scanning ? "Scanning…" : "Scan jobcards (.xls)"}
              </Button>
              <Button variant="outline" className="h-9" onClick={() => exportReport("xlsx")} title="Export Excel report" data-testid="hc-export-xlsx">
                <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel
              </Button>
              <Button variant="outline" className="h-9" onClick={() => exportReport("pdf")} title="Export PDF report" data-testid="hc-export-pdf">
                <FileText className="w-4 h-4 mr-1.5" /> PDF
              </Button>
              <Button variant="outline" className="h-9" onClick={toggleArchive} title={selected.archived ? "Restore this report" : "Archive this report"} data-testid="hc-archive-btn">
                <Archive className="w-4 h-4 mr-1.5" /> {selected.archived ? "Unarchive" : "Archive"}
              </Button>
              <Button variant="outline" className="h-9 border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={removeReport} data-testid="hc-delete-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {counts.total > 0 && (
            <div className="px-4 pt-3" data-testid="hc-progress">
              <div className={`grid gap-x-6 gap-y-2 ${groupCounts.nrc.total > 0 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
                <div data-testid="hc-progress-planned">
                  <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-gray-500">
                    <span>Jobcards completion</span>
                    <span className="text-emerald-700 font-bold" data-testid="hc-progress-pct">{groupCounts.planned.pct}% · {groupCounts.planned.done}/{groupCounts.planned.total}</span>
                  </div>
                  <div className="mt-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${groupCounts.planned.pct}%` }} data-testid="hc-progress-fill" />
                  </div>
                </div>
                {groupCounts.nrc.total > 0 && (
                  <div data-testid="hc-progress-nrc">
                    <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-gray-500">
                      <span>NRC completion</span>
                      <span className="text-amber-700 font-bold" data-testid="hc-progress-nrc-pct">{groupCounts.nrc.pct}% · {groupCounts.nrc.done}/{groupCounts.nrc.total}</span>
                    </div>
                    <div className="mt-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${groupCounts.nrc.pct}%` }} data-testid="hc-progress-nrc-fill" />
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-gray-500" data-testid="hc-parts-line-counter">
                Part lines: <span className="font-bold text-[#1f3864]">{(selected.parts || []).length}</span>
              </div>
            </div>
          )}

          {counts.total > 0 && (
            <div className="px-4 py-2 border-b border-summit-hairline flex items-center gap-2 flex-wrap bg-summit-canvas/40" data-testid="hc-counts">
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{counts.total} jobcards</span>
              {["completed", "in_progress", "postponed", "open"].map((s) => (
                <span key={s} className={`px-2 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-widest ${HC_META[s].cls}`} data-testid={`hc-count-${s}`}>
                  {HC_META[s].label} {counts[s]}
                </span>
              ))}
              {counts.removed > 0 && (
                <span className="px-2 py-0.5 rounded-full border border-gray-300 bg-gray-100 text-gray-500 font-mono text-[10px] uppercase tracking-widest" data-testid="hc-count-removed">
                  Removed {counts.removed}
                </span>
              )}
            </div>
          )}

          <div className="px-4 pt-2 border-b border-summit-hairline flex items-center gap-1" data-testid="hc-section-tabs">
            {[["jobcards", `Jobcards (${groups.planned.length})`], ["nrc", `NRC (${groups.nrc.length})`], ["parts", `Parts (${(selected.parts || []).length})`]].map(([key, label]) => (
              <button
                key={key}
                className={`px-4 py-2 font-mono text-[11px] uppercase tracking-widest border-b-2 -mb-px transition-colors ${section === key ? "border-summit-primary text-summit-primary font-bold" : "border-transparent text-gray-400 hover:text-gray-600"}`}
                onClick={() => setSection(key)}
                data-testid={`hc-tab-${key}`}
              >
                {label}
              </button>
            ))}
          </div>

          {section === "parts" ? (
            <PartsSection hc={selected} onChanged={replaceCheck} />
          ) : (section === "nrc" ? groups.nrc : groups.planned).length === 0 ? (
            <p className="text-sm text-gray-400 py-10 text-center" data-testid="hc-no-jobcards">
              {section === "nrc"
                ? "No NRC jobcards yet — click “Scan jobcards (.xls)” and choose NRC when prompted."
                : `No jobcards yet — click “Scan jobcards (.xls)” and pick the JobCardStatus export for ${selected.tail}.`}
            </p>
          ) : (
            <>
              <div className="px-4 py-2 border-b border-summit-hairline flex items-center gap-2" data-testid="hc-search-bar">
                <Search className="w-4 h-4 text-gray-400 shrink-0" />
                <Input
                  className="h-8 max-w-sm text-sm"
                  placeholder="Search jobcards — JC#, details, subject, work type…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="hc-search-input"
                />
                {search && (
                  <button className="p-1 rounded text-gray-400 hover:text-gray-600" onClick={() => setSearch("")} data-testid="hc-search-clear">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
                <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-gray-400" data-testid="hc-search-count">
                  {sectionCards.length} shown
                </span>
                <Button size="sm" variant="outline" className="h-8 border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={deleteAllJobcards} data-testid="hc-delete-all-jc">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete all
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-summit-hairline font-mono text-[10px] uppercase tracking-widest text-gray-500">
                      <th className="py-2 px-3">JC#</th>
                      <th className="py-2 px-3">Job details / subject area</th>
                      <th className="py-2 px-3">File status</th>
                      <th className="py-2 px-3">Work type</th>
                      <th className="py-2 px-3 text-right">Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectionCards.map((jc) => (
                      <JobcardRow key={jc.id} hc={selected} jc={jc} onChanged={replaceCheck} />
                    ))}
                    {sectionCards.length === 0 && (
                      <tr><td colSpan={5} className="text-center py-8 text-sm text-gray-400" data-testid="hc-search-empty">No jobcards match “{search}”.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
          </div>
        </div>
      )}

      <Dialog open={!!scanPrompt} onOpenChange={(o) => { if (!o && !applying) setScanPrompt(null); }}>
        <DialogContent className="max-w-md" data-testid="hc-scan-dialog">
          {scanPrompt && !scanPrompt.group ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Scan jobcards — choose group</DialogTitle>
                <DialogDescription>
                  <span className="font-bold text-summit-primary">{scanPrompt.cards.length}</span> jobcards found in the file. Which group should they be scanned into?
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <button
                  className="rounded-lg border-2 border-summit-hairline hover:border-summit-primary p-4 text-left transition-colors"
                  onClick={() => chooseGroup("planned")}
                  data-testid="hc-scan-group-planned"
                >
                  <ClipboardCheck className="w-5 h-5 text-summit-primary" />
                  <div className="font-display font-semibold text-sm mt-2">Jobcards</div>
                  <div className="text-xs text-gray-500 mt-0.5">Planned workscope cards</div>
                </button>
                <button
                  className="rounded-lg border-2 border-summit-hairline hover:border-amber-500 p-4 text-left transition-colors"
                  onClick={() => chooseGroup("nrc")}
                  data-testid="hc-scan-group-nrc"
                >
                  <ClipboardList className="w-5 h-5 text-amber-600" />
                  <div className="font-display font-semibold text-sm mt-2">NRC</div>
                  <div className="text-xs text-gray-500 mt-0.5">Non-routine / unplanned cards</div>
                </button>
              </div>
            </>
          ) : scanPrompt ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Confirm scan — {scanPrompt.group === "nrc" ? "NRC" : "Jobcards"}</DialogTitle>
                <DialogDescription>Only the differences will be applied — existing progress statuses are preserved.</DialogDescription>
              </DialogHeader>
              <div className="flex flex-wrap gap-2" data-testid="hc-scan-diff-chips">
                <span className="px-2 py-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 font-mono text-[11px] font-bold" data-testid="hc-scan-added-chip">+{scanPrompt.diff.added.length} added</span>
                <span className="px-2 py-1 rounded-full border border-rose-300 bg-rose-50 text-rose-800 font-mono text-[11px] font-bold" data-testid="hc-scan-removed-chip">−{scanPrompt.diff.removed.length} removed</span>
                {scanPrompt.diff.restored.length > 0 && (
                  <span className="px-2 py-1 rounded-full border border-sky-300 bg-sky-50 text-sky-800 font-mono text-[11px] font-bold" data-testid="hc-scan-restored-chip">{scanPrompt.diff.restored.length} restored</span>
                )}
                <span className="px-2 py-1 rounded-full border border-gray-300 bg-gray-50 text-gray-600 font-mono text-[11px]" data-testid="hc-scan-updated-chip">{scanPrompt.diff.updated} unchanged/updated</span>
              </div>
              {scanPrompt.diff.added.length > 0 && (
                <div data-testid="hc-scan-added-list">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-emerald-700">New jobcards</div>
                  <div className="text-xs text-gray-600 mt-0.5 max-h-20 overflow-y-auto">
                    {scanPrompt.diff.added.slice(0, 30).join(", ")}{scanPrompt.diff.added.length > 30 ? ` +${scanPrompt.diff.added.length - 30} more` : ""}
                  </div>
                </div>
              )}
              {scanPrompt.diff.removed.length > 0 && (
                <div data-testid="hc-scan-removed-list">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-rose-700">Missing from file — will be kept &amp; flagged “Removed from workscope”</div>
                  <div className="text-xs text-gray-600 mt-0.5 max-h-20 overflow-y-auto">
                    {scanPrompt.diff.removed.slice(0, 30).join(", ")}{scanPrompt.diff.removed.length > 30 ? ` +${scanPrompt.diff.removed.length - 30} more` : ""}
                  </div>
                </div>
              )}
              {scanPrompt.diff.added.length === 0 && scanPrompt.diff.removed.length === 0 && scanPrompt.diff.restored.length === 0 && (
                <p className="text-xs text-gray-500" data-testid="hc-scan-no-diff">No new or removed jobcards — file details will refresh on existing cards.</p>
              )}
              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1 h-9" disabled={applying} onClick={() => setScanPrompt(null)} data-testid="hc-scan-cancel">Cancel</Button>
                <Button className="flex-1 h-9 bg-summit-primary hover:bg-summit-primary-soft text-white" disabled={applying} onClick={applyScan} data-testid="hc-scan-confirm">
                  {applying ? "Applying…" : "Apply scan"}
                </Button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-sm" data-testid="hc-new-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">New heavy check report</DialogTitle>
            <DialogDescription>Pick the aircraft, then scan its JobCardStatus .xls export.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Aircraft *</span>
              <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={newTail} onChange={(e) => setNewTail(e.target.value)} data-testid="hc-new-tail">
                <option value="">Select aircraft…</option>
                {aircraft.map((a) => <option key={a.tail} value={a.tail}>{a.tail}</option>)}
              </select>
            </div>
            <div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Report title</span>
              <Input className="mt-1" placeholder={newTail ? `${newTail} Heavy Check` : "e.g. FSRJ Heavy Check 2026"} value={newTitle} onChange={(e) => setNewTitle(e.target.value)} data-testid="hc-new-title" />
            </div>
            <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={create} disabled={busy} data-testid="hc-new-confirm">Confirm</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
